<template>
  <div class="container-fluid">
    <div class="row justify-content-md-center">
      <div class="col-10">
        <h2>Products</h2>
      </div>
    </div>
    <div class="row justify-content-md-center">
      <div class="col-10">
        <b-tabs class="mt-3">
          <b-tab title="Test Tools">
            <div class="mt-3">
              <b-form-input v-model="filters.testTools" placeholder="Filter by serial..." class="mb-2" @input="refreshTable('testToolsTable')"></b-form-input>
              <b-table ref="testToolsTable" :items="fetchTestTools" :fields="testToolsFields"
                :current-page="pages.testTools" :per-page="perPage"
                :striped="true" :hover="true" :outlined="true" :small="true" :busy.sync="busy.testTools" show-empty>
                <template v-slot:cell(product)="data">{{ data.item.product ? data.item.product.pr_code : '-' }}</template>
                <template v-slot:cell(hw_version)="data">{{ data.item.hardwareVersion ? data.item.hardwareVersion.hw_revision : '-' }}</template>
                <template v-slot:cell(fw_version)="data">{{ data.item.firmware ? data.item.firmware.fw_version : '-' }}</template>
                <template v-slot:cell(produced_at)="data">{{ data.item.produced_at | formatDate }}</template>
                <template v-slot:cell(calibration_exp)="data">{{ data.item.calibration_exp | formatDate }}</template>
                <template v-slot:table-busy><div class="text-center my-2"><b-spinner small></b-spinner> Loading...</div></template>
              </b-table>
              <b-pagination v-if="totals.testTools > perPage" v-model="pages.testTools" :total-rows="totals.testTools" :per-page="perPage" align="center" @change="refreshTable('testToolsTable')"></b-pagination>
            </div>
          </b-tab>

          <b-tab title="Part Serials">
            <div class="mt-3">
              <b-form-input v-model="filters.partSerials" placeholder="Filter by serial..." class="mb-2" @input="refreshTable('partSerialsTable')"></b-form-input>
              <b-table ref="partSerialsTable" :items="fetchPartSerials" :fields="partSerialsFields"
                :current-page="pages.partSerials" :per-page="perPage"
                :striped="true" :hover="true" :outlined="true" :small="true" :busy.sync="busy.partSerials" show-empty>
                <template v-slot:cell(product)="data">{{ data.item.product ? data.item.product.pr_code : '-' }}</template>
                <template v-slot:cell(hw_version)="data">{{ data.item.hardwareVersion ? data.item.hardwareVersion.hw_revision : '-' }}</template>
                <template v-slot:cell(fw_version)="data">{{ data.item.firmware ? data.item.firmware.fw_version : '-' }}</template>
                <template v-slot:cell(produced_by)="data">{{ data.item.producedBy ? data.item.producedBy.name : '-' }}</template>
                <template v-slot:cell(released_by)="data">{{ data.item.releasedBy ? data.item.releasedBy.username : '-' }}</template>
                <template v-slot:cell(produced_at)="data">{{ data.item.produced_at | formatDate }}</template>
                <template v-slot:table-busy><div class="text-center my-2"><b-spinner small></b-spinner> Loading...</div></template>
              </b-table>
              <b-pagination v-if="totals.partSerials > perPage" v-model="pages.partSerials" :total-rows="totals.partSerials" :per-page="perPage" align="center" @change="refreshTable('partSerialsTable')"></b-pagination>
            </div>
          </b-tab>

          <b-tab title="Part Reports">
            <div class="mt-3">
              <b-form-row class="mb-2">
                <b-col>
                  <b-form-input v-model="filters.partReports" placeholder="Filter by test type or result..." @input="refreshTable('partReportsTable')"></b-form-input>
                </b-col>
                <b-col cols="auto">
                  <b-form-input type="date" v-model="filters.partReportsDate" @input="refreshTable('partReportsTable')"></b-form-input>
                </b-col>
                <b-col cols="auto">
                  <b-button variant="outline-secondary" :disabled="!filters.partReportsDate" @click="downloadPartReports">
                    <octicon name="desktop-download"></octicon> Download
                  </b-button>
                </b-col>
              </b-form-row>
              <b-table ref="partReportsTable" :items="fetchPartReports" :fields="partReportsFields"
                :current-page="pages.partReports" :per-page="perPage"
                :striped="true" :hover="true" :outlined="true" :small="true" :busy.sync="busy.partReports" show-empty>
                <template v-slot:cell(serial)="data">{{ data.item.partSerial ? data.item.partSerial.serial : '-' }}</template>
                <template v-slot:cell(operator)="data">{{ data.item.operator ? data.item.operator.username : '-' }}</template>
                <template v-slot:cell(production_date)="data">{{ data.item.production_date | formatDate }}</template>
                <template v-slot:cell(result)="data">
                  <b-badge :variant="data.item.result === 'PASS' ? 'success' : data.item.result === 'FAIL' ? 'danger' : 'secondary'">{{ data.item.result }}</b-badge>
                </template>
                <template v-slot:cell(test_results)="row">
                  <a v-if="canViewTestResults" href="#" @click.prevent.stop="row.toggleDetails">
                    {{ row.detailsShowing ? 'Hide' : 'Show' }}
                    <octicon :name="row.detailsShowing ? 'chevron-up' : 'chevron-down'"></octicon>
                  </a>
                  <span v-else class="text-muted">—</span>
                </template>
                <template v-slot:row-details="row">
                  <b-card v-if="canViewTestResults" border-variant="secondary" bg-variant="light" class="rounded-0">
                    <strong class="d-block mb-2">Test Results for Report #{{ row.item.id }}</strong>
                    <b-table v-if="row.item.results && row.item.results.length" :items="row.item.results" :fields="testResultsFields" :small="true" :striped="true" :outlined="true" class="mb-0">
                      <template v-slot:cell(result)="data">
                        <b-badge :variant="resultVariant(data.item.result)">{{ data.item.result }}</b-badge>
                      </template>
                      <template v-slot:cell(why)="data">
                        <span v-if="failureSummary(data.item)" class="text-danger">{{ failureSummary(data.item) }}</span>
                        <span v-else class="text-muted">—</span>
                      </template>
                      <template v-slot:cell(details)="data">
                        <a v-if="hasFailureDetail(data.item)" href="#" @click.prevent.stop="data.toggleDetails">{{ data.detailsShowing ? 'Hide' : 'Details' }}</a>
                      </template>
                      <template v-slot:row-details="data">
                        <div class="p-2 bg-white border rounded small">
                          <div v-if="data.item.fail_reason_text" class="mb-1"><strong>Reason:</strong> {{ data.item.fail_reason_text }}</div>
                          <div v-if="data.item.reason" class="mb-1"><strong>Note:</strong> {{ data.item.reason }}</div>
                          <div v-if="data.item.errorMessage" class="mb-1 text-danger"><strong>Error:</strong> {{ data.item.errorMessage }}</div>
                          <div v-if="hasContent(data.item.fail_reason)" class="mb-1">
                            <strong>Failed checks:</strong>
                            <ul class="mb-0">
                              <li v-for="(fr, i) in asArray(data.item.fail_reason)" :key="i">{{ stringify(fr) }}</li>
                            </ul>
                          </div>
                          <div v-if="hasContent(data.item.data)" class="mb-1"><strong>Measured data:</strong><pre class="mb-0">{{ pretty(data.item.data) }}</pre></div>
                          <div v-if="hasContent(data.item.rules)" class="mb-1"><strong>Rules:</strong><pre class="mb-0">{{ pretty(data.item.rules) }}</pre></div>
                        </div>
                      </template>
                    </b-table>
                    <p v-else class="text-muted mb-0">No individual test results recorded.</p>
                  </b-card>
                </template>
                <template v-slot:table-busy><div class="text-center my-2"><b-spinner small></b-spinner> Loading...</div></template>
              </b-table>
              <b-pagination v-if="totals.partReports > perPage" v-model="pages.partReports" :total-rows="totals.partReports" :per-page="perPage" align="center" @change="refreshTable('partReportsTable')"></b-pagination>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Octicon from 'vue-octicon/components/Octicon.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'ProductManagement',
  components: { Octicon },
  computed: {
    ...mapGetters(['hasRole']),
    // Same three roles as the routes and the router guard
    canViewTestResults() {
      return this.hasRole('administrator') || this.hasRole('support') || this.hasRole('supplier')
    }
  },
  filters: {
    formatDate(value) {
      if (value) return moment(String(value)).format('YYYY-MM-DD HH:mm')
      return '-'
    }
  },
  data() {
    return {
      perPage: 15,
      filters: {
        testTools: '',
        partSerials: '',
        partReports: '',
        partReportsDate: ''
      },
      pages: {
        testTools: 1,
        partSerials: 1,
        partReports: 1
      },
      totals: {
        testTools: 0,
        partSerials: 0,
        partReports: 0
      },
      busy: {
        testTools: false,
        partSerials: false,
        partReports: false
      },
      testToolsFields: [
        { key: 'id', label: '#' },
        { key: 'serial', label: 'Serial', sortable: true },
        { key: 'product', label: 'Product' },
        { key: 'hw_version', label: 'HW Version' },
        { key: 'fw_version', label: 'FW Version' },
        { key: 'produced_at', label: 'Produced At', sortable: true },
        { key: 'calibration_exp', label: 'Calibration Exp.' },
        { key: 'updated_at', label: 'Updated', sortable: true }
      ],
      partSerialsFields: [
        { key: 'id', label: '#' },
        { key: 'serial', label: 'Serial', sortable: true },
        { key: 'product', label: 'Product' },
        { key: 'hw_version', label: 'HW Version' },
        { key: 'fw_version', label: 'FW Version' },
        { key: 'produced_at', label: 'Produced At', sortable: true },
        { key: 'produced_by', label: 'Produced By' },
        { key: 'released_by', label: 'Released By' },
        { key: 'updated_at', label: 'Updated', sortable: true }
      ],
      partReportsFields: [
        { key: 'id', label: '#' },
        { key: 'serial', label: 'Serial' },
        { key: 'test_type', label: 'Test Type', sortable: true },
        { key: 'result', label: 'Result', sortable: true },
        { key: 'duration', label: 'Duration' },
        { key: 'production_date', label: 'Date', sortable: true },
        { key: 'operator', label: 'Operator' },
        { key: 'test_results', label: 'Test Results' },
        { key: 'updated_at', label: 'Updated', sortable: true }
      ],
      testResultsFields: [
        { key: 'test_id', label: 'Test ID' },
        { key: 'result', label: 'Result' },
        { key: 'description', label: 'Description' },
        { key: 'why', label: 'Why' },
        { key: 'details', label: '' }
      ]
    }
  },
  methods: {
    refreshTable(ref) {
      if (this.$refs[ref]) this.$refs[ref].refresh()
    },

    resultVariant(result) {
      const r = String(result || '').toUpperCase()
      if (r === 'PASS') return 'success'
      if (r === 'FAIL') return 'danger'
      return 'secondary'
    },
    hasContent(v) {
      if (v === null || v === undefined || v === '') return false
      if (Array.isArray(v)) return v.length > 0
      if (typeof v === 'object') return Object.keys(v).length > 0
      return true
    },
    asArray(v) {
      if (Array.isArray(v)) return v
      return v === null || v === undefined ? [] : [v]
    },
    stringify(v) {
      return v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v)
    },
    pretty(v) {
      try { return JSON.stringify(v, null, 2) } catch (e) { return String(v) }
    },
    failureSummary(item) {
      if (item.fail_reason_text) return item.fail_reason_text
      if (this.hasContent(item.fail_reason)) return this.asArray(item.fail_reason).map(this.stringify).join('; ')
      if (item.errorMessage) return item.errorMessage
      if (item.reason) return item.reason
      return ''
    },
    // Whether there's anything worth expanding in the Details drill-down
    hasFailureDetail(item) {
      return !!(item.fail_reason_text || item.reason || item.errorMessage ||
        this.hasContent(item.fail_reason) || this.hasContent(item.data) || this.hasContent(item.rules))
    },

    fetchTestTools() {
      this.busy.testTools = true
      return this.$http.get('/test-tools', {
        params: { currentPage: this.pages.testTools, perPage: this.perPage, filter: this.filters.testTools }
      }).then(r => { this.totals.testTools = parseInt(r.data.total); this.busy.testTools = false; return r.data.data })
        .catch(() => { this.busy.testTools = false; return [] })
    },
    fetchPartSerials() {
      this.busy.partSerials = true
      return this.$http.get('/part-serials', {
        params: { currentPage: this.pages.partSerials, perPage: this.perPage, filter: this.filters.partSerials }
      }).then(r => { this.totals.partSerials = parseInt(r.data.total); this.busy.partSerials = false; return r.data.data })
        .catch(() => { this.busy.partSerials = false; return [] })
    },
    fetchPartReports() {
      this.busy.partReports = true
      return this.$http.get('/part-reports', {
        params: {
          currentPage: this.pages.partReports,
          perPage: this.perPage,
          filter: this.filters.partReports,
          date: this.filters.partReportsDate || undefined
        }
      }).then(r => { this.totals.partReports = parseInt(r.data.total); this.busy.partReports = false; return r.data.data })
        .catch(() => { this.busy.partReports = false; return [] })
    },
    downloadPartReports() {
      const date = this.filters.partReportsDate
      if (!date) return
      this.$http.get('/part-reports/export', { params: { date }, responseType: 'blob' }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        // Named after the production date the file holds, not the date of the download
        link.setAttribute('download', `PCBA_test_results_${date}.json`)
        document.body.appendChild(link)
        link.click()
      }).catch(console.error)
    }
  }
}
</script>
